/**
 * Manufacturer Field Registry
 * Single source of truth for all Manufacturer field definitions — drives
 * grid columns, Manage Columns order, Advanced Search, and (Sub-task 9.3)
 * Excel export via each field's `surfaces` membership (issue #653, Task 9 —
 * applies the same pattern Products adopted in issue #585).
 *
 * `group`/`surfaces` field-surface audit (Sub-task 9.1 AC): before this
 * task, every registered field appeared on every surface unconditionally —
 * `toDataGridColumns()`/`toModalFields()` both iterated the whole registry
 * with no filtering, and only `isSearchable` gated Advanced Search. That
 * meant "registered" and "shown everywhere" were the same thing. Adding
 * `surfaces` lets that differ: the 13 new fields below are grid/search/
 * export-surfaced but deliberately NOT modal-surfaced yet — Task 10 (not
 * yet built) replaces the current single-form modal with dedicated Names/
 * Details/Status tabs, and that task is what actually decides where each
 * new field belongs in that new UI. Adding them to `surfaces: [...,
 * 'modal']` now would make them appear as raw, unstyled text/boolean
 * fields in the CURRENT generic modal ahead of that real design work —
 * `toModalFields()` (see the adapter) now filters by `surfaces.includes
 * ('modal')`, so today's modal renders exactly the same fields it did
 * before this task.
 *
 * NOTE: The following fields are intentionally excluded or have special handling:
 * - manufacturer_id: Auto-generated, used as rowId, shown only in modal view mode
 * - product_count: Computed field derived from products.length
 * - facility_count: Computed field derived from facilities.length
 * - CEP linkage is deliberately NOT registered as a field here: unlike the
 *   other 7 sources, CEP certificates are facility-level (Facility_CEPs)
 *   with no corresponding is_cep_linked column on Manufacturers (see
 *   sql/migrations/V1_3_64's own header) — a manufacturer's CEP status is a
 *   rollup across its facilities computed at query time, not a stored
 *   scalar this registry can point at. (The AC text says "eight" linkage
 *   flags; only 7 are real stored columns — see docs/features/
 *   manufacturer-alignment.md's Sub-task 9.1 verification note.)
 */

import { Manufacturer } from '../../services/api/domains/manufacturers/types';
import {
  BaseFieldDefinition,
  FieldSurface,
} from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';

// Type alias for type-safe field definitions
type ManufacturerFieldDefinition = BaseFieldDefinition<Manufacturer>;

/**
 * Complete Manufacturer Field Registry
 * Order matches the group taxonomy (`group` on each field) so grid column
 * order, Manage Columns order, and Advanced Search field order all derive
 * from the same order.
 */
export const MANUFACTURER_FIELDS_REGISTRY: ManufacturerFieldDefinition[] = [
  // ========== Core Information ==========
  {
    key: 'manufacturer_name',
    label: 'Manufacturer Name',
    group: 'Core Information',
    // 'modal' dropped in Task 10, Sub-task 10.1 — the raw text field this
    // surface used to render is superseded by NamesTab.tsx's grouped
    // variant list + preferred-name selection (writes via
    // PUT /:id/preferred-name, the same path updateManufacturer() already
    // redirects a manufacturer_name edit through). Keeping 'modal' here
    // would leave two editable entry points writing through the same
    // Manual/short_display row.
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isRequired: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    validation: {
      minLength: 2,
      maxLength: 255,
    },
    renderConfig: {
      width: 300,
      flex: 1,
    },
  },

  // ========== Status ==========
  {
    key: 'lgm_visibility',
    label: 'LGM Visibility',
    group: 'Status',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isEditable: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      "LGM-set visibility — distinct from any source system's own approval/active state. ACTIVE and RETAINED both show by default; ARCHIVED is hidden unless this field is filtered or the manufacturer is found by name.",
    selectionOptions: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'RETAINED', label: 'Retained' },
      { value: 'ARCHIVED', label: 'Archived' },
    ],
    renderConfig: { width: 130 },
  },
  {
    key: 'vendor_type',
    label: 'Vendor Type',
    group: 'Status',
    // No default filter (Sub-task 9.1 AC) — filterable, but the grid/search
    // config here carries no preset value, so every vendor type shows
    // unfiltered until a user explicitly filters.
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isEditable: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'Confirmed final 2026-08-18 (Open Question 13): single type per vendor, 7-value enum matches the AML exactly (sql/migrations/V1_3_64).',
    selectionOptions: [
      { value: 'API Manufacturer', label: 'API Manufacturer' },
      { value: 'CDMO/CMO/CRO', label: 'CDMO/CMO/CRO' },
      { value: 'Bio/Pharma', label: 'Bio/Pharma' },
      { value: 'Specialty Pharma', label: 'Specialty Pharma' },
      { value: 'Generics (ANDA)', label: 'Generics (ANDA)' },
      { value: 'Reseller/Distributor', label: 'Reseller/Distributor' },
      { value: 'Other', label: 'Other' },
    ],
    renderConfig: { width: 180 },
  },
  {
    key: 'aml_facility_approval',
    label: 'AML Facility Approval',
    group: 'Status',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isEditable: false,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      "Whether at least one of this manufacturer's facilities is approved on the SAP Approved Manufacturer List (AML), from its linked SAP AML vendor records (one per site) in the latest snapshot. Read-only and kept current automatically; distinct from LGM Visibility (set by LGM) and from any single facility's status.",
    selectionOptions: [
      { value: 'HAS APPROVED FACILITY', label: 'Has approved facility' },
      { value: 'NO APPROVED FACILITY', label: 'No approved facility' },
      { value: 'NO AML FACILITY', label: 'No AML facility' },
    ],
    renderConfig: { width: 200 },
  },

  // ========== Identifiers ==========
  {
    key: 'lei_code',
    label: 'LEI',
    group: 'Identifiers',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description: 'GLEIF Legal Entity Identifier.',
    renderConfig: { width: 180 },
  },
  {
    key: 'lei_entity_status',
    label: 'LEI Entity Status',
    group: 'Identifiers',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      "This company's operating status for its Legal Entity Identifier (LEI), per GLEIF (the Global Legal Entity Identifier Foundation) — e.g. ACTIVE. Kept separate from LEI Registration Status; a LAPSED registration can coexist with an ACTIVE entity (Decision 12).",
    selectionOptions: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'INACTIVE', label: 'Inactive' },
    ],
    renderConfig: { width: 140 },
  },
  {
    key: 'lei_registration_status',
    label: 'LEI Registration Status',
    group: 'Identifiers',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    // Task 13, Sub-task 13.2 (Decision 12): the grid column tooltip must
    // carry the same LAPSED-≠-defunct clarification StatusSection.tsx's
    // modal tooltip already has — "every status-like value... wherever the
    // value is surfaced: grid cell, modal panel, chip, filter label", not
    // just the modal.
    description:
      "The registration lifecycle of this company's Legal Entity Identifier (LEI), per GLEIF (the Global Legal Entity Identifier Foundation) — e.g. ISSUED, LAPSED, RETIRED. LAPSED means the LEI registration itself has not been renewed — it does NOT mean the company is defunct. Check LEI Entity Status for the company's actual operating state.",
    // GLEIF's official Common Data File registration-status code list —
    // the full finite set, not just the 3 values seen in this dev dataset.
    selectionOptions: [
      { value: 'ISSUED', label: 'Issued' },
      { value: 'LAPSED', label: 'Lapsed' },
      { value: 'ANNULLED', label: 'Annulled' },
      { value: 'PENDING_TRANSFER', label: 'Pending Transfer' },
      { value: 'PENDING_ARCHIVAL', label: 'Pending Archival' },
      { value: 'DUPLICATE', label: 'Duplicate' },
      { value: 'RETIRED', label: 'Retired' },
      { value: 'MERGED', label: 'Merged' },
      { value: 'CANCELLED', label: 'Cancelled' },
      { value: 'TRANSFERRED', label: 'Transferred' },
    ],
    renderConfig: { width: 160 },
  },
  {
    key: 'lei_legal_country',
    label: 'LEI Legal Country',
    group: 'Identifiers',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 140 },
  },
  {
    key: 'lei_incorporation_date',
    label: 'LEI Incorporation Date',
    group: 'Identifiers',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'date',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: { width: 170, formatAs: 'dateonly' },
  },
  {
    key: 'duns_number',
    label: 'DUNS (Company)',
    group: 'Identifiers',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    description:
      'Company-level DUNS number — distinct from Facilities.duns_number (facility/site-level).',
    renderConfig: { width: 150 },
  },
  {
    key: 'source_of_record',
    label: 'Source of Record',
    group: 'Identifiers',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'SAP', label: 'SAP' },
      { value: 'Salesforce', label: 'Salesforce' },
      { value: 'FDA', label: 'FDA' },
      { value: 'LilitImport', label: 'Lilit Import' },
      { value: 'Manual', label: 'Manual' },
    ],
    renderConfig: { width: 140 },
  },

  // ========== Hierarchy ==========
  {
    key: 'parent_manufacturer_id',
    label: 'Parent Manufacturer ID',
    group: 'Hierarchy',
    surfaces: ['grid', 'export'],
    dataType: 'number',
    isSortable: true,
    isFilterable: true,
    // NOT searchable here -- toSearchFieldConfig's generic scalar branch
    // would otherwise emit this as a raw numeric text input, forcing a
    // user to know/type a real id. The Advanced Search field for this is
    // instead a hand-written async-select in searchConfigAdapter.ts
    // (PARENT_MANUFACTURER_ID_CONFIG), scoped server-side to manufacturers
    // that are actually a parent today.
    isSearchable: false,
    // Task 13, Sub-task 13.2 (Decision 12: "Dedup pointer vs. corporate
    // parent... a dual-FK trap"): the description must distinguish this
    // from canonical_id explicitly, not just describe itself in isolation
    // — canonical_id means "merged away, look at that record instead";
    // parent_manufacturer_id means "a real, separate, still-active
    // corporate subsidiary/parent relationship." Confusing the two would
    // read a live subsidiary as a merge tombstone.
    description:
      'Self-referencing FK to the corporate parent, where known — a real, separate, still-active company relationship. NOT the same as canonical_id (the merge/dedup pointer, which means this record was merged away).',
    renderConfig: { width: 170 },
  },

  // ========== External Linkage ==========
  // Per-source linkage flags (Requirement 7). 7 real stored columns, not 8
  // — see this file's header comment for why CEP has no column here.
  {
    key: 'is_decrs_linked',
    label: 'DECRS Linked',
    group: 'External Linkage',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 130 },
  },
  {
    key: 'is_gleif_linked',
    label: 'GLEIF Linked',
    group: 'External Linkage',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 130 },
  },
  {
    key: 'is_sap_aml_linked',
    label: 'SAP AML Linked',
    group: 'External Linkage',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 130 },
  },
  {
    key: 'is_salesforce_linked',
    label: 'Salesforce Linked',
    group: 'External Linkage',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 140 },
  },
  {
    key: 'is_usdmf_linked',
    label: 'USDMF Linked',
    group: 'External Linkage',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 130 },
  },
  {
    key: 'is_orangebook_linked',
    label: 'Orange Book Linked',
    group: 'External Linkage',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 150 },
  },
  {
    key: 'is_ndc_linked',
    label: 'NDC Linked',
    group: 'External Linkage',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'boolean',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    selectionOptions: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    renderConfig: { width: 130 },
  },

  // ========== Relational Fields ==========
  {
    key: 'facilities',
    label: 'Facilities',
    group: 'Relational Fields',
    // 'modal' dropped in Task 10, Sub-task 10.5 — the bare async_multiselect
    // this surface used to render is superseded by FacilitiesPanel.tsx
    // (same reasoning as manufacturer_name's own surfaces comment, Sub-task
    // 10.1 — keeping both would duplicate the same list in one Details tab).
    surfaces: ['grid', 'search', 'export'],
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isEditable: true, // Can be edited in modal
    isSortable: false,
    isFilterable: true,
    isSearchable: true, // Flattened to facility_name for search
    relationConfig: {
      type: 'many-to-many',
      entityName: 'facilities',
      idField: 'facility_id',
      displayField: 'facility_name',
      searchField: 'facility_name',
      dataSource: 'api',
      apiEndpoint: '/facilities',
    },
    renderConfig: {
      width: 300,
      maxDisplayItems: 3,
    },
  },
  {
    key: 'products',
    label: 'Products',
    group: 'Relational Fields',
    // 'modal' dropped — retroactive fix carried into Sub-task 10.5's commit.
    // Sub-task 10.4 added ProductsPanel.tsx but never removed 'products'
    // from this surface, so the Details tab was rendering the bare
    // async_multiselect AND the rich ProductsPanel back-to-back — the same
    // duplicate-UI class of bug the manufacturer_name/facilities fixes
    // address, just missed in that commit. Caught while researching this
    // sub-task, not by a test.
    surfaces: ['grid', 'search', 'export'],
    dataType: 'array',
    primitiveType: 'object',
    isRelation: true,
    isEditable: true, // Can be edited in modal
    isSortable: false,
    isFilterable: true,
    isSearchable: true,
    description: 'Products associated with this manufacturer',
    relationConfig: {
      type: 'many-to-many',
      entityName: 'products',
      idField: 'product_id',
      // display_name is the precedence-derived name (Product Name
      // Architecture, issue #470/#472) — the authoritative field to show/
      // search by. product_cas_name/preferred_product_name are legacy
      // inputs to that precedence and are frequently null on their own.
      displayField: 'display_name',
      searchField: 'display_name',
      dataSource: 'api',
      apiEndpoint: '/products',
    },
    renderConfig: {
      width: 300,
      maxDisplayItems: 3,
    },
  },

  // ========== Computed Fields ==========
  {
    key: 'product_count',
    label: 'Product Count',
    group: 'Computed Fields',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'number',
    isComputed: true,
    computedFrom: 'products',
    isSortable: true,
    isFilterable: true, // Backend supports COUNT subquery filtering
    isSearchable: true, // Available in advanced search
    isEditable: false,
    description:
      'Number of products associated with this manufacturer. Manufacturers with no products are hidden by default unless this field is filtered or the manufacturer is found by name.',
    renderConfig: {
      width: 150,
    },
  },
  {
    key: 'facility_count',
    label: 'Facility Count',
    group: 'Computed Fields',
    surfaces: ['grid', 'search', 'export'],
    dataType: 'number',
    isComputed: true,
    computedFrom: 'facilities',
    isSortable: true,
    isFilterable: true, // Backend supports COUNT subquery filtering
    isSearchable: true, // Available in advanced search
    isEditable: false,
    description: 'Number of facilities associated with this manufacturer',
    renderConfig: {
      width: 150,
    },
  },

  // ========== Metadata Fields ==========
  {
    key: 'user_id',
    label: 'Created By',
    group: 'Metadata',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'string',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 200,
    },
  },
  {
    key: 'created_date',
    label: 'Created Date',
    group: 'Metadata',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
  {
    key: 'modified_date',
    label: 'Modified Date',
    group: 'Metadata',
    surfaces: ['grid', 'search', 'modal', 'export'],
    dataType: 'date',
    isReadOnly: true,
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    renderConfig: {
      width: 180,
      formatAs: 'datetime',
    },
  },
];

/**
 * Filter the registry to fields appearing on a given surface (issue #653,
 * Task 9 — mirrors Products' `getFieldsForSurface`, issue #585). Grid
 * columns, Manage Columns order, and (Sub-task 9.3) Excel export all derive
 * from filtering this registry by surface instead of independently-
 * maintained lists.
 */
export function getFieldsForSurface(
  surface: FieldSurface
): ManufacturerFieldDefinition[] {
  return MANUFACTURER_FIELDS_REGISTRY.filter(f =>
    f.surfaces?.includes(surface)
  );
}

// Create field helpers using shared utility
const helpers = createFieldHelpers<Manufacturer>(MANUFACTURER_FIELDS_REGISTRY);

export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;
